import { useEffect } from 'react';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs';

import type { SiteUserTraits } from './preloadSiteUserTraits';
import { formatTapTraits } from './formatTapTraits';

export type SiteUserTraitsState = {
	data: SiteUserTraits | null;
	loading: boolean;
	error: Error | null;
};

const initialState: SiteUserTraitsState = {
	data: null,
	loading: false,
	error: null,
};

const actions = {
	start:
		() =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ loading: true, error: null });
		},
	success:
		(data: SiteUserTraits) =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ data, loading: false, error: null });
		},
	error:
		(err: Error) =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ error: err, loading: false });
		},
	reset:
		() =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ data: null, loading: false, error: null });
		},
};

export const siteUserTraitsStore = createStore({
	initialState,
	actions,
	name: 'siteUserTraitsStore',
});

export const useSiteUserTraitsStore = createHook(siteUserTraitsStore);
export const useSiteUserTraitsActions = createActionsHook(siteUserTraitsStore);

async function fetchSiteUserTraitsData(cloudId: string, userId: string): Promise<SiteUserTraits> {
	const res = await cfetch(
		`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`,
		{ method: 'GET' },
	);
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`Request failed with ${res.status}: ${text || res.statusText}`);
	}
	return (await res.json()) as SiteUserTraits;
}

// Use a module-level variable to ensure we only fetch once per app lifecycle
// This way multiple components using the hook won't trigger multiple fetches
let hasFetched: boolean = false;

// Test utility to reset the hasFetched flag
export function resetHasFetched() {
	hasFetched = false;
}

function getSiteUserTraitsFromSources(): SiteUserTraits | null {
	if (typeof window !== 'undefined' && window.__SSR_SITE_USER_TRAITS__) {
		return window.__SSR_SITE_USER_TRAITS__;
	}

	const { tapContext } = getSSRRenderInputs();
	const attributes = formatTapTraits(tapContext?.siteUser);
	if (attributes) {
		const data = { attributes };
		// Write to window during SSR (JSDOM provides window polyfill in SSR environment)
		// This data is then read by generateNonStreamingGlobals() and serialized to client
		// This ensures consistency across SSR -> hydration -> SPA navigation
		if (typeof window !== 'undefined') {
			window.__SSR_SITE_USER_TRAITS__ = data;
		}
		return data;
	}

	return null;
}

export function useSiteUserTraits({ skipBootstrap }: { skipBootstrap?: boolean } = {}): {
	data: SiteUserTraits | null;
	loading: boolean;
	error: Error | null;
} {
	const preloadedData = getSiteUserTraitsFromSources();

	const [{ data, loading, error }, storeActions] = useSiteUserTraitsStore();
	const { accessStatus, cloudId, userId } = useSessionData();
	const hasLicensedAccess =
		accessStatus === 'LICENSED_USE_ACCESS' || accessStatus === 'LICENSED_ADMIN_ACCESS';

	useEffect(() => {
		if (preloadedData || data || loading || error) return;

		if (hasFetched || skipBootstrap) return;
		if (!hasLicensedAccess || !cloudId || !userId) return;

		hasFetched = true;
		storeActions.start();
		fetchSiteUserTraitsData(cloudId, userId).then(storeActions.success).catch(storeActions.error);
	}, [
		skipBootstrap,
		preloadedData,
		data,
		loading,
		error,
		storeActions,
		hasLicensedAccess,
		cloudId,
		userId,
	]);

	if (preloadedData) {
		return { data: preloadedData, loading: false, error: null };
	}

	return { data, loading, error };
}
